import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import axios from "axios";
import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { TwMainLayout } from "../../../components/tailwind";
import TwTextEditer from "../../../components/tailwind/TwTextEditer";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForClasses, getUrlForCronServer } from "../../../utils/confighelpers";
import { FieldError, FieldLabel, Icon, inputClass } from "../schedule/ui";
import "../../../styles/onboarding.css";

/**
 * News Feeds (new design). Routes /newsfeed and /newsfeed/detail?id= (old links open the post's preview).
 * Same endpoints as before: newsfeed/list, newsfeed/add, newsfeed/update/:id, DELETE newsfeed/:id.
 * Push notifications are sent by the backend: on first publish, or on an update when notifyMembers is true.
 */

const PAGE_SIZE = 24;
const MAX_PHOTO_MB = 8;
const CATEGORY_SUGGESTIONS = ["Schedule", "Announcement", "Event", "Tip"];
const EMPTY_POST = { topicName: "", topicCategory: "", topicSummary: "", topicContent: "", topicImage: [], status: "draft" };

const ICONS = {
  plus: <path d="M12 5v14M5 12h14" />,
  news: <><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" /><path d="M18 14h-8M15 18h-5M10 6h8v4h-8z" /></>,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>,
  send: <><path d="m22 2-7 20-4-9-9-4z" /><path d="M22 2 11 13" /></>,
  undo: <><path d="M9 14 4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-3" /></>,
  trash: <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />,
  phone: <><rect x="6" y="2" width="12" height="20" rx="3" /><path d="M11 18h2" /></>,
  bell: <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" /></>,
  image: <><rect x="3" y="5" width="18" height="14" rx="3" /><circle cx="9" cy="10" r="2" /><path d="m21 15-4.5-4.5L7 19" /></>,
  warn: <><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></>,
  close: <path d="M18 6 6 18M6 6l12 12" />,
};
const DOTS = <><circle cx="12" cy="5" r="1.6" fill="currentColor" /><circle cx="12" cy="12" r="1.6" fill="currentColor" /><circle cx="12" cy="19" r="1.6" fill="currentColor" /></>;

const FILTERS = [
  ["all", "All"],
  ["published", "Published"],
  ["draft", "Drafts"],
];

/** Plain text of the editor's HTML (for excerpts and the "is it empty" check). */
const plainText = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = String(html).replace(/<br\s*\/?>|<\/(p|li|h[1-6]|div|blockquote)>/gi, " ");
  return (div.textContent || "").replace(/ /g, " ").replace(/\s+/g, " ").trim();
};
const photoOf = (p) => (Array.isArray(p?.topicImage) ? p.topicImage[0] : typeof p?.topicImage === "string" ? p.topicImage : "") || "";
const isPublished = (p) => p?.status === "published";
const fmtDay = (d) => (d ? moment(d).format("D MMM YYYY") : "");

const apiOk = (res) => res?.status === 200 && res?.data?.statusCode === 200;
const apiMessage = (res, fallback) => (typeof res?.data?.message === "string" && res.data.message && res.data.message !== "success" ? res.data.message : fallback);

/** Fields sent on add / update (the same fields the old page sent). */
const postBody = (p, status, extra = {}) => ({
  topicName: p.topicName,
  topicCategory: p.topicCategory || "",
  topicSummary: p.topicSummary || "",
  topicContent: p.topicContent,
  topicImage: p.topicImage,
  status,
  ...extra,
});

export default function NewsFeedPage({ title = "News Feeds" }) {
  const navigate = useNavigate();
  const { addSuccessToast, addErrorToast } = useToast();
  const userReduxState = useSelector(userState);
  const clubName = userReduxState.userData?.myBusinessProfile?.businessName || userReduxState.userData?.fullName || "Your club";
  const clubLogo = userReduxState.userData?.myBusinessProfile?.logo || userReduxState.userData?.profilePic || "";

  const [posts, setPosts] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);
  const [filter, setFilter] = React.useState("all");
  const [editor, setEditor] = React.useState({ open: false, post: null, key: 0 });
  const [previewing, setPreviewing] = React.useState(null);
  const [deleting, setDeleting] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);

  const load = React.useCallback(async (pageNo = 1) => {
    pageNo === 1 ? setLoading(true) : setLoadingMore(true);
    setLoadError(false);
    try {
      const res = await axiosInstance({ method: "get", url: `${getUrlForClasses("")}newsfeed/list?page=${pageNo}&limit=${PAGE_SIZE}` });
      if (apiOk(res)) {
        const data = Array.isArray(res.data.data?.data) ? res.data.data.data : [];
        setPosts((prev) => {
          if (pageNo === 1) return data;
          const seen = new Set(prev.map((p) => p._id));
          return [...prev, ...data.filter((p) => !seen.has(p._id))];
        });
        setTotal(res.data.data?.total || 0);
        setPage(pageNo);
      } else if (pageNo === 1) setLoadError(true);
    } catch (e) {
      if (pageNo === 1) setLoadError(true);
      else addErrorToast("Couldn't load more posts.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    load(1);
  }, [load]);

  // Old links (/newsfeed/detail?id=…) open the post's preview.
  React.useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("id");
    if (!id) return;
    (async () => {
      try {
        const res = await axiosInstance({ method: "get", url: `${getUrlForClasses("")}newsfeed/list?newsFeedId=${id}` });
        const p = apiOk(res) ? (Array.isArray(res.data.data) ? res.data.data[0] : res.data.data) : null;
        if (p && p._id) setPreviewing(p);
        else addErrorToast("That post doesn't exist any more.");
      } catch (e) {
        addErrorToast("Couldn't open that post.");
      }
      navigate("/newsfeed", { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = React.useMemo(() => ({ all: posts.length, published: posts.filter(isPublished).length, draft: posts.filter((p) => !isPublished(p)).length }), [posts]);
  const visible = filter === "all" ? posts : posts.filter((p) => (filter === "published" ? isPublished(p) : !isPublished(p)));
  const hasMore = posts.length < total;
  const categories = React.useMemo(() => {
    const used = posts.map((p) => (p.topicCategory || "").trim()).filter(Boolean);
    return [...new Set([...used, ...CATEGORY_SUGGESTIONS])].slice(0, 8);
  }, [posts]);

  const openEditor = (post) => setEditor((e) => ({ open: true, post, key: e.key + 1 }));

  /** Publish / move to drafts from the card menu or the delete dialog (sends the whole post, like the editor). */
  const setStatus = async (p, status) => {
    setBusyId(p._id);
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForClasses("")}newsfeed/update/${p._id}`, data: postBody(p, status) });
      if (apiOk(res)) {
        setPosts((list) => list.map((x) => (x._id === p._id ? { ...x, status, updatedAt: new Date().toISOString() } : x)));
        addSuccessToast(status === "published" ? "Published. Your members get a notification." : "Moved to drafts. Members no longer see it.");
        return true;
      }
      addErrorToast(apiMessage(res, "Couldn't update the post."));
    } catch (e) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setBusyId(null);
    }
    return false;
  };

  const remove = async () => {
    const p = deleting;
    setBusyId(p._id);
    try {
      const res = await axiosInstance.delete(`${getUrlForClasses("")}newsfeed/${p._id}`, {});
      if (res?.data?.statusCode === 200) {
        setPosts((list) => list.filter((x) => x._id !== p._id));
        setTotal((t) => Math.max(0, t - 1));
        addSuccessToast("Post deleted");
        setDeleting(null);
      } else addErrorToast(apiMessage(res, "Couldn't delete the post."));
    } catch (e) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const noPostsAtAll = !loading && !loadError && posts.length === 0;

  return (
    <TwMainLayout
      children={
        <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-3xl">{title}</h1>
              <p className="mt-1 max-w-2xl text-sm text-gray-600 sm:text-base">Posts your members read in the FitClub app. Publishing a post sends them a notification.</p>
            </div>
            <button
              type="button"
              onClick={() => openEditor(null)}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brown-coffee px-5 py-3 text-base font-extrabold text-white shadow-[0_10px_24px_rgba(72,27,58,0.25)] hover:bg-light-brown-coffee sm:w-auto"
            >
              <Icon className="h-5 w-5" d={ICONS.plus} />
              New post
            </button>
          </div>

          {noPostsAtAll ? (
            <div className="mt-8 flex flex-col items-center rounded-3xl border border-dashed border-[#E3D0F7] bg-white px-6 py-14 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz">
                <Icon className="h-8 w-8" d={ICONS.news} />
              </span>
              <h2 className="mt-5 text-xl font-black text-gray-900">No posts yet</h2>
              <p className="mt-2 max-w-md text-sm text-gray-600 sm:text-base">Share schedule changes, events and tips. Members see your posts in the FitClub app.</p>
              <button type="button" onClick={() => openEditor(null)} className="mt-6 rounded-2xl bg-brown-coffee px-5 py-3 text-sm font-extrabold text-white hover:bg-light-brown-coffee">
                Write your first post
              </button>
            </div>
          ) : (
            <>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <div role="tablist" aria-label="Filter posts" className="flex gap-1 overflow-x-auto rounded-2xl bg-[#F3EEF5] p-1">
                  {FILTERS.map(([k, l]) => (
                    <button
                      key={k}
                      type="button"
                      role="tab"
                      aria-selected={filter === k}
                      onClick={() => setFilter(k)}
                      className={classNames("h-9 shrink-0 whitespace-nowrap rounded-xl px-3.5 text-sm font-extrabold", filter === k ? "bg-white text-brown-coffee shadow-sm" : "text-gray-500 hover:text-gray-900")}
                    >
                      {l} <span className="opacity-60">{loading ? "" : counts[k]}</span>
                    </button>
                  ))}
                </div>
                <span className="text-sm font-bold text-gray-600 sm:ml-auto">{loading ? "" : `${total} ${total === 1 ? "post" : "posts"}`}</span>
              </div>

              {loading ? (
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="animate-pulse overflow-hidden rounded-3xl border border-gray-200 bg-white">
                      <div className="aspect-[16/9] bg-[#EDE5F0]" />
                      <div className="flex flex-col gap-3 p-5">
                        <span className="h-3 w-32 rounded-full bg-[#F3EEF5]" />
                        <span className="h-5 w-56 max-w-full rounded-full bg-[#EDE5F0]" />
                        <span className="h-3 w-full rounded-full bg-[#F3EEF5]" />
                        <span className="h-3 w-2/3 rounded-full bg-[#F3EEF5]" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : loadError ? (
                <div className="mt-5 rounded-3xl border border-gray-200 bg-white px-6 py-12 text-center">
                  <p className="text-sm font-bold text-gray-700">Couldn't load your posts.</p>
                  <button type="button" onClick={() => load(1)} className="mt-3 rounded-xl px-4 py-2.5 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
                    Try again
                  </button>
                </div>
              ) : visible.length === 0 ? (
                <div className="mt-5 rounded-3xl border border-dashed border-[#E3D0F7] bg-white px-6 py-12 text-center">
                  <p className="text-base font-black text-gray-900">{filter === "draft" ? "No drafts" : "Nothing published yet"}</p>
                  {hasMore && <p className="mt-1 text-sm text-gray-600">Load more posts to see older ones.</p>}
                </div>
              ) : (
                <ul className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {visible.map((p) => (
                    <PostCard
                      key={p._id}
                      post={p}
                      busy={busyId === p._id}
                      onEdit={() => openEditor(p)}
                      onPreview={() => setPreviewing(p)}
                      onToggleStatus={() => setStatus(p, isPublished(p) ? "draft" : "published")}
                      onDelete={() => setDeleting(p)}
                    />
                  ))}
                </ul>
              )}

              {!loading && hasMore && (
                <div className="mt-5 flex justify-center">
                  <button type="button" disabled={loadingMore} onClick={() => load(page + 1)} className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF] disabled:opacity-60">
                    {loadingMore && <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#E3D0F7] border-t-indigo-bz" />}
                    Show more posts
                  </button>
                </div>
              )}
            </>
          )}

          <PostEditor
            key={editor.key}
            open={editor.open}
            post={editor.post}
            categories={categories}
            clubName={clubName}
            clubLogo={clubLogo}
            onClose={() => setEditor((e) => ({ ...e, open: false }))}
            onSaved={() => load(1)}
          />

          <PreviewDialog
            post={previewing}
            clubName={clubName}
            clubLogo={clubLogo}
            onClose={() => setPreviewing(null)}
            onEdit={() => {
              const p = previewing;
              setPreviewing(null);
              openEditor(p);
            }}
          />

          <DeleteDialog
            post={deleting}
            busy={!!deleting && busyId === deleting._id}
            onClose={() => setDeleting(null)}
            onDelete={remove}
            onDraft={async () => {
              if (await setStatus(deleting, "draft")) setDeleting(null);
            }}
          />
        </div>
      }
    />
  );
}

function PostCard({ post: p, busy, onEdit, onPreview, onToggleStatus, onDelete }) {
  const pub = isPublished(p);
  const photo = photoOf(p);
  return (
    <li className={classNames("flex flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-[0_10px_30px_rgba(72,27,58,0.05)]", busy && "pointer-events-none opacity-60")}>
      <button type="button" onClick={onEdit} aria-label={`Edit ${p.topicName}`} className="relative block aspect-[16/9] w-full overflow-hidden bg-[#EDE5F0]">
        {photo ? <img src={photo} alt="" className="h-full w-full object-cover transition-transform duration-300 hover:scale-[1.02]" /> : <Icon className="absolute inset-0 m-auto h-10 w-10 text-[#B9B1BB]" d={ICONS.image} />}
        <span className={classNames("absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-extrabold shadow-sm", pub ? "bg-[#E8F7EE] text-[#1F6B3A]" : "bg-white text-gray-700")}>{pub ? "Published" : "Draft"}</span>
      </button>
      <div className="flex flex-1 flex-col gap-2 px-5 pb-1 pt-4">
        <div className="flex flex-wrap items-center gap-2 text-xs font-extrabold text-gray-500">
          {p.topicCategory && <span className="rounded-full bg-[#F3E9FF] px-2.5 py-0.5 text-[#6A00BA]">{p.topicCategory}</span>}
          <span>{fmtDay(p.updatedAt || p.createdAt)}</span>
        </div>
        <h2 className="line-clamp-2 break-words text-lg font-black leading-snug tracking-tight text-gray-900">{p.topicName}</h2>
        <p className="line-clamp-2 text-sm leading-snug text-gray-600">{plainText(p.topicContent)}</p>
      </div>
      <div className="flex items-center justify-between px-3 pb-3 pl-5 pt-2">
        <button type="button" onClick={onPreview} className="-ml-2 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
          <Icon className="h-4 w-4" d={ICONS.phone} />
          Preview in app
        </button>
        <Menu as="div" className="relative">
          <MenuButton aria-label={`Options for ${p.topicName}`} className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900">
            <Icon d={DOTS} />
          </MenuButton>
          <MenuItems anchor="top end" className="z-[60] mb-1 w-56 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
            <MenuItem>
              <button type="button" onClick={onEdit} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]">
                <Icon className="h-[18px] w-[18px] text-gray-500" d={ICONS.edit} /> Edit
              </button>
            </MenuItem>
            <MenuItem>
              <button type="button" onClick={onToggleStatus} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]">
                <Icon className="h-[18px] w-[18px] text-gray-500" d={pub ? ICONS.undo : ICONS.send} /> {pub ? "Move to drafts" : "Publish now"}
              </button>
            </MenuItem>
            <MenuItem>
              <button type="button" onClick={onDelete} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-[#B4232F] data-[focus]:bg-[#FFF5F6]">
                <Icon className="h-[18px] w-[18px]" d={ICONS.trash} /> Delete
              </button>
            </MenuItem>
          </MenuItems>
        </Menu>
      </div>
    </li>
  );
}

/** The post as members see it in the app: photo, club, date, title, text. */
function AppPost({ post, clubName, clubLogo, compact = false }) {
  const photo = photoOf(post);
  const text = post.topicContent && plainText(post.topicContent) ? post.topicContent : "";
  return (
    <div className="flex h-full flex-col overflow-hidden bg-white">
      <div className={classNames("w-full shrink-0 bg-[#EDE5F0]", compact ? "h-40" : "h-56")}>{photo && <img src={photo} alt="" className="h-full w-full object-cover" />}</div>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-4">
        <div className="flex items-center gap-2">
          {clubLogo ? <img src={clubLogo} alt="" className="h-6 w-6 rounded-lg object-cover" /> : <span className="h-6 w-6 rounded-lg bg-portland-orange" />}
          <span className="truncate text-xs font-extrabold text-gray-600">
            {clubName} · {fmtDay(post.updatedAt || post.createdAt) || "Today"}
          </span>
        </div>
        <p className="break-words text-lg font-black leading-tight text-gray-900">{post.topicName || "Your title"}</p>
        {text ? (
          <div className="break-words text-sm leading-relaxed text-gray-700 [&_a]:text-indigo-bz [&_a]:underline [&_li]:ml-4 [&_ol]:list-decimal [&_ul]:list-disc" dangerouslySetInnerHTML={{ __html: text }} />
        ) : (
          <p className="text-sm text-gray-400">Your post appears here as you type.</p>
        )}
      </div>
    </div>
  );
}

function Phone({ children, size = "md" }) {
  return (
    <div className={classNames("rounded-[40px] bg-[#1d1320] p-2.5 shadow-[0_24px_50px_rgba(29,19,32,0.25)]", size === "lg" ? "w-[330px]" : "w-[280px]")}>
      <div className={classNames("overflow-hidden rounded-[31px] bg-white", size === "lg" ? "h-[640px]" : "h-[540px]")}>{children}</div>
    </div>
  );
}

function PostEditor({ open, post, categories, clubName, clubLogo, onClose, onSaved }) {
  const { addSuccessToast, addErrorToast } = useToast();
  const editing = !!post?._id;
  const wasPublished = isPublished(post);
  const [f, setF] = React.useState(() => ({ ...EMPTY_POST, ...(post || {}), topicImage: post?.topicImage ? (Array.isArray(post.topicImage) ? post.topicImage : [post.topicImage]) : [] }));
  const [errors, setErrors] = React.useState({});
  const [busy, setBusy] = React.useState("");
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const [notify, setNotify] = React.useState(false);
  const fileRef = React.useRef(null);
  const initialHtml = React.useRef(post?.topicContent || "");

  const set = (patch) => {
    setF((v) => ({ ...v, ...patch }));
    const keys = Object.keys(patch);
    if (keys.some((k) => errors[k])) setErrors((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !keys.includes(k))));
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp|gif)$/i.test(file.type)) return setErrors((e) => ({ ...e, topicImage: "Use a JPG, PNG or WebP image." }));
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) return setErrors((e) => ({ ...e, topicImage: `Images up to ${MAX_PHOTO_MB} MB, please.` }));
    const data = new FormData();
    data.append("uploadingFor", "TestClass");
    data.append("file", file);
    setUploading(true);
    try {
      const res = await axios.request({ method: "post", maxBodyLength: Infinity, url: `${getUrlForCronServer()}media-upload`, headers: { "Content-Type": "multipart/form-data" }, data });
      if (res?.data?.url) set({ topicImage: [res.data.url] });
      else setErrors((e) => ({ ...e, topicImage: "Upload failed. Try another image." }));
    } catch (err) {
      setErrors((e) => ({ ...e, topicImage: err?.response?.data?.error || "Upload failed. Try again." }));
    } finally {
      setUploading(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!photoOf(f)) e.topicImage = "Add a cover photo. The app shows one on every post.";
    if (!f.topicName.trim()) e.topicName = "Give the post a title.";
    if (!plainText(f.topicContent)) e.topicContent = "Write something for your members.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async (status) => {
    if (uploading || !validate()) return;
    const data = postBody({ ...f, topicName: f.topicName.trim(), topicCategory: (f.topicCategory || "").trim() }, status, editing && wasPublished && status === "published" && notify ? { notifyMembers: true } : {});
    setBusy(status);
    try {
      const res = await axiosInstance({
        method: "post",
        url: editing ? `${getUrlForClasses("")}newsfeed/update/${post._id}` : `${getUrlForClasses("")}newsfeed/add`,
        data,
      });
      if (apiOk(res)) {
        const msg =
          status === "draft"
            ? wasPublished
              ? "Moved to drafts. Members no longer see it."
              : "Saved as draft"
            : wasPublished
              ? notify
                ? "Saved. Your members get a notification."
                : "Changes saved"
              : "Published. Your members get a notification.";
        addSuccessToast(msg);
        onSaved?.();
        onClose();
      } else addErrorToast(apiMessage(res, "Couldn't save the post. Please try again."));
    } catch (e) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const photo = photoOf(f);
  const anyBusy = !!busy || uploading;

  return (
    <Dialog open={open} onClose={() => !anyBusy && onClose()} className="relative z-50">
      <DialogBackdrop transition className="fixed inset-0 bg-[#1d1320]/45 transition-opacity duration-300 ease-out data-[closed]:opacity-0" />
      <div className="fixed inset-0 overflow-hidden">
        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-10">
          <DialogPanel transition className="pointer-events-auto flex h-full w-screen max-w-5xl transform flex-col bg-[#F7F4F8] shadow-2xl transition duration-300 ease-out data-[closed]:translate-x-full sm:rounded-l-3xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4 sm:rounded-tl-3xl sm:px-7 sm:py-5">
              <div className="min-w-0">
                <p className="text-xs font-extrabold tracking-wide text-portland-orange">{editing ? (wasPublished ? "EDIT PUBLISHED POST" : "EDIT DRAFT") : "NEW POST"}</p>
                <DialogTitle className="mt-0.5 truncate text-xl font-black tracking-tight text-gray-900 sm:text-2xl">{editing ? f.topicName || "Post" : "New post"}</DialogTitle>
                <p className="mt-1 text-sm text-gray-600">{wasPublished ? "Changes show in the app as soon as you save." : "Save it as a draft, or publish it to your members now."}</p>
              </div>
              <button type="button" onClick={() => !anyBusy && onClose()} aria-label="Close" className="-mr-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900">
                <Icon d={ICONS.close} />
              </button>
            </div>

            <div className="flex min-h-0 flex-1">
              <div className="flex min-w-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-6 sm:px-7">
                {/* Photo */}
                <section className="rounded-3xl border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-5">
                  <FieldLabel>Cover photo</FieldLabel>
                  <div
                    onDragOver={(ev) => {
                      ev.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(ev) => {
                      ev.preventDefault();
                      setDragOver(false);
                      uploadPhoto(ev.dataTransfer.files?.[0]);
                    }}
                    className={classNames(
                      "relative flex aspect-[16/9] w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed transition-colors",
                      errors.topicImage ? "border-red-300 bg-red-50" : dragOver ? "border-indigo-bz bg-[#F3E9FF]" : photo ? "border-transparent" : "border-gray-200 bg-[#FBF8FE]"
                    )}
                  >
                    {photo && <img src={photo} alt="Cover" className="absolute inset-0 h-full w-full object-cover" />}
                    {uploading ? (
                      <div className="relative flex flex-col items-center gap-2 rounded-2xl bg-white/90 px-5 py-4">
                        <span className="h-7 w-7 animate-spin rounded-full border-[3px] border-[#E3D0F7] border-t-indigo-bz" />
                        <span className="text-sm font-bold text-gray-700">Uploading…</span>
                      </div>
                    ) : photo ? (
                      <div className="absolute bottom-3 right-3 flex gap-2">
                        <button type="button" onClick={() => fileRef.current?.click()} className="rounded-xl bg-white/95 px-3 py-2 text-sm font-extrabold text-gray-900 shadow hover:bg-white">
                          Replace
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => fileRef.current?.click()} className="flex flex-col items-center gap-2 px-6 text-center">
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz">
                          <Icon d={ICONS.image} />
                        </span>
                        <span className="text-sm font-extrabold text-gray-900">Drop a photo or click to upload</span>
                        <span className="text-xs text-gray-500">Landscape works best · JPG, PNG or WebP · up to {MAX_PHOTO_MB} MB</span>
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={(ev) => {
                      uploadPhoto(ev.target.files?.[0]);
                      ev.target.value = "";
                    }}
                  />
                  <FieldError>{errors.topicImage}</FieldError>
                </section>

                {/* Title + category */}
                <section className="flex flex-col gap-4 rounded-3xl border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-5">
                  <div>
                    <FieldLabel htmlFor="nf-title">Title</FieldLabel>
                    <input id="nf-title" className={inputClass(errors.topicName)} value={f.topicName} maxLength={120} onChange={(e) => set({ topicName: e.target.value })} placeholder="e.g. New yoga class on Sunday mornings" />
                    <FieldError>{errors.topicName}</FieldError>
                  </div>
                  <div>
                    <FieldLabel htmlFor="nf-cat" optional>
                      Category
                    </FieldLabel>
                    <input id="nf-cat" className={inputClass(false)} value={f.topicCategory} maxLength={40} onChange={(e) => set({ topicCategory: e.target.value })} placeholder="e.g. Schedule" />
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {categories.map((c) => {
                        const on = (f.topicCategory || "").trim().toLowerCase() === c.toLowerCase();
                        return (
                          <button
                            key={c}
                            type="button"
                            aria-pressed={on}
                            onClick={() => set({ topicCategory: on ? "" : c })}
                            className={classNames("h-8 rounded-full border px-3 text-xs font-extrabold", on ? "border-indigo-bz bg-[#F3E9FF] text-[#6A00BA]" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50")}
                          >
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </section>

                {/* Content */}
                <section className="rounded-3xl border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-5">
                  <FieldLabel>Post</FieldLabel>
                  <div className={classNames("[&>div]:!max-w-none [&>div]:!rounded-2xl [&>div]:!border-gray-200", errors.topicContent && "[&>div]:!border-red-300")}>
                    <TwTextEditer name="topicContent" initialValue={initialHtml.current} onChange={(state, html) => set({ topicContent: html })} />
                  </div>
                  <FieldError>{errors.topicContent}</FieldError>
                </section>
              </div>

              {/* What members see */}
              <aside className="hidden w-[340px] shrink-0 flex-col items-center gap-3 overflow-y-auto border-l border-gray-200 bg-[#F1ECF3] px-6 py-6 lg:flex">
                <p className="text-xs font-extrabold uppercase tracking-wide text-gray-500">What members see</p>
                <Phone>
                  <AppPost post={f} clubName={clubName} clubLogo={clubLogo} compact />
                </Phone>
                <p className="max-w-[260px] text-center text-xs text-gray-500">The category only shows here on the web, not in the app.</p>
              </aside>
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:rounded-bl-3xl sm:px-7">
              {wasPublished ? (
                <label className="flex cursor-pointer items-center gap-2.5 text-sm font-bold text-gray-700">
                  <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="h-5 w-5 rounded border-gray-300 text-indigo-bz focus:ring-indigo-bz" />
                  Notify members about this update
                </label>
              ) : (
                <span className="flex items-center gap-2 text-sm text-gray-600">
                  <Icon className="h-[18px] w-[18px] text-indigo-bz" d={ICONS.bell} />
                  Publishing sends your members a notification.
                </span>
              )}
              <div className="flex flex-wrap items-center justify-end gap-2">
                <button type="button" onClick={onClose} disabled={anyBusy} className="rounded-xl px-4 py-3 text-sm font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-50">
                  Cancel
                </button>
                <button type="button" onClick={() => save("draft")} disabled={anyBusy} className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-extrabold text-gray-900 hover:bg-gray-50 disabled:opacity-60">
                  {busy === "draft" && <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />}
                  {wasPublished ? "Move to drafts" : "Save as draft"}
                </button>
                <button type="button" onClick={() => save("published")} disabled={anyBusy} className="flex min-w-32 items-center justify-center gap-2 rounded-2xl bg-brown-coffee px-6 py-3 text-base font-extrabold text-white hover:bg-light-brown-coffee disabled:opacity-60">
                  {busy === "published" && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                  {wasPublished ? "Save changes" : "Publish"}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

function PreviewDialog({ post, clubName, clubLogo, onClose, onEdit }) {
  const [last, setLast] = React.useState(post);
  React.useEffect(() => {
    if (post) setLast(post);
  }, [post]);
  const p = post || last;
  return (
    <Dialog open={!!post} onClose={onClose} className="relative z-[60]">
      <DialogBackdrop transition className="fixed inset-0 bg-[#1d1320]/55 transition-opacity duration-200 data-[closed]:opacity-0" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel transition className="flex flex-col items-center gap-4 transition duration-200 data-[closed]:scale-95 data-[closed]:opacity-0">
            <DialogTitle className="sr-only">Preview of {p?.topicName}</DialogTitle>
            {p && (
              <Phone size="lg">
                <AppPost post={p} clubName={clubName} clubLogo={clubLogo} />
              </Phone>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-extrabold text-gray-900 hover:bg-gray-50">
                Close
              </button>
              <button type="button" onClick={onEdit} className="rounded-2xl bg-brown-coffee px-5 py-3 text-sm font-extrabold text-white hover:bg-light-brown-coffee">
                Edit post
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

function DeleteDialog({ post, busy, onClose, onDelete, onDraft }) {
  const [last, setLast] = React.useState(post);
  React.useEffect(() => {
    if (post) setLast(post);
  }, [post]);
  const p = post || last;
  const pub = isPublished(p);
  return (
    <Dialog open={!!post} onClose={() => !busy && onClose()} className="relative z-[60]">
      <DialogBackdrop transition className="fixed inset-0 bg-[#1d1320]/45 transition-opacity duration-200 data-[closed]:opacity-0" />
      <div className="fixed inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4">
        <DialogPanel transition className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl transition duration-200 data-[closed]:translate-y-4 data-[closed]:opacity-0 sm:rounded-3xl">
          <div className="flex items-start gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FFE8EB] text-[#B4232F]">
              <Icon d={ICONS.warn} />
            </span>
            <div className="min-w-0">
              <DialogTitle className="break-words text-lg font-black text-gray-900">Delete “{p?.topicName || "this post"}”?</DialogTitle>
              <p className="mt-1.5 text-sm text-gray-600">
                {pub ? "It disappears from the app for all members. If you only want to take it down for now, move it to drafts instead." : "This draft will be gone for good."}
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" disabled={busy} onClick={onClose} className="rounded-xl px-4 py-3 text-sm font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-50">
              Keep it
            </button>
            {pub && (
              <button type="button" disabled={busy} onClick={onDraft} className="rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-extrabold text-gray-900 hover:bg-gray-50 disabled:opacity-50">
                Move to drafts instead
              </button>
            )}
            <button type="button" disabled={busy} onClick={onDelete} className="flex items-center justify-center gap-2 rounded-2xl bg-[#B4232F] px-5 py-3 text-sm font-extrabold text-white hover:bg-[#9a1d28] disabled:opacity-60">
              {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
              Delete
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
